import React from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";
import * as D from "draft-js";
import * as Router from "next/router";
import getVideoId from "get-video-id";

import * as gtag from "../utils/gtag";
import * as u from "../utils";
import * as T from "./Typography";
import * as I from "./Icons";
import Loading from "./Loading";
import ImageInitials from "./ImageInitials";
import * as UserContext from "./UserContext";
import TextAreaRich from "./TextAreaRich";
import { Button } from "./Button";
import colours from "../utils/colours";
import Input from "./Input";

export default function WhatsYourMind(props) {
  const userInfo = React.useContext(UserContext.Context);
  const router = Router.useRouter();
  const inputFileRef = React.useRef();
  const currStateRef = React.useRef();
  const [isOpen, setIsOpen] = React.useState(false);
  const [isActive, setIsActive] = React.useState();
  const [files, setFiles] = React.useState([]);
  const [fileUploadError, setFileUploadError] = React.useState();
  const [isLoading, setIsLoading] = React.useState(false);
  const [videoUrl, setVideoUrl] = React.useState("");
  const [videoUrlVisible, setVideoUrlVisible] = React.useState(false);
  const [video, setVideo] = React.useState({});
  if (!isOpen) return <Placeholder userInfo={userInfo} setIsOpen={setIsOpen} />;

  return (
    <S.Container>
      {isLoading && (
        <S.InnerLoading>
          <Loading />
        </S.InnerLoading>
      )}
      <S.InnerContainer isOpen={isOpen}>
        <S.HeaderContainer>
          <ImageInitials size={"mid"} {...userInfo} />
          <S.SubHeader>
            <T.H5>{userInfo.name}</T.H5>
            <T.CS>Posting to {props.location || "Feed"}</T.CS>
          </S.SubHeader>
          <S.CloseContainer
            onClick={() => {
              setIsOpen(false), setVideoUrl(""), setVideo({});
            }}
          >
            <I.Close />
          </S.CloseContainer>
        </S.HeaderContainer>
        <TextAreaRich
          style={{ marginBottom: 15 }}
          placeholder={props.placeholder || "What's on your mind?"}
          onEditorChange={onEditorChange}
        />
        <S.PreviewContainer count={files.length}>
          {files.map(getSinglePreview)}
        </S.PreviewContainer>
        {video.src && (
          <S.VideoPreviewContainer>
            <S.VideoPreview src={video.src} />
          </S.VideoPreviewContainer>
        )}
      </S.InnerContainer>
      <S.BottomContainer>
        <S.IconsContainer>
          <S.IconContainer onClick={onOpenPhoto}>
            <I.Camera />
            <S.InputFile
              onClick={(evt) => evt.stopPropagation()}
              onChange={onMemoryLoad}
              type="file"
              multiple={true}
              ref={inputFileRef}
              accept="image/*"
            />
          </S.IconContainer>
          <S.IconContainer onClick={() => setVideoUrlVisible(!videoUrlVisible)}>
            <I.PlayCircle grow={0.22} />
          </S.IconContainer>
        </S.IconsContainer>
        <S.Error>{fileUploadError}</S.Error>
        <S.PostButton size="sml" onClick={onCreatePost} isDisable={!isActive}>
          POST
        </S.PostButton>
      </S.BottomContainer>
      <S.InputVideoContainer isVisible={videoUrlVisible}>
        <S.InputVideo
          value={videoUrl}
          onChange={(e) => validateVideoUrl(e.target.value)}
          hint="Paste video URL here"
        />
      </S.InputVideoContainer>
    </S.Container>
  );

  function getSinglePreview(elm, idx) {
    return (
      <S.PreviewInner key={idx}>
        <S.PreviewRemove onClick={onRemove}>
          <I.Close />
        </S.PreviewRemove>
        <S.PreviewImage src={elm.data} />
      </S.PreviewInner>
    );

    function onRemove() {
      setFiles((prev) => {
        return prev.filter((innerElm) => {
          return innerElm !== elm;
        });
      });
    }
  }

  function onEditorChange(editorState) {
    const currContent = editorState.getCurrentContent();
    const currText = currContent.getPlainText();
    currStateRef.current = editorState;

    if (currText && !isActive) setIsActive(true);
    if (!currText && isActive) setIsActive(false);
  }

  function onOpenPhoto() {
    inputFileRef.current.click();
  }

  function onMemoryLoad(evt) {
    setFileUploadError(undefined);
    const { files } = evt.target;

    for (let file of files) {
      const FILE_SIZE_LIMIT_IN_BYTES = 5242880;
      if (file.size > FILE_SIZE_LIMIT_IN_BYTES) {
        setFileUploadError(
          `The image ${file.name} is too large. The limit is 5MB.`
        );
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const data = reader.result;
        setVideo({});
        setFiles((prev) => prev.concat({ data, file }));
      };
      reader.readAsDataURL(file);
    }
  }

  async function onCreatePost() {
    const editorState = currStateRef.current;
    if (!(props.onCreatePost && editorState)) return;

    setIsLoading(true);
    const content = editorState.getCurrentContent();
    const currText = content.getPlainText();
    const data = D.convertToRaw(content);
    if (!currText?.trim()) return setIsLoading(false);
    await props.onCreatePost(currText, data, files, video);

    gtag.event({
      name: gtag.GA_EVENT.CONNECT_ENGAGEMENT,
      category: "Connection",
      label: "Create post",
      value: router.route,
    });

    return ReactDOM.unstable_batchedUpdates(() => {
      setIsOpen(false);
      setIsLoading(false);
      setFiles([]);
      setVideo({});
      setVideoUrl("");
    });
  }

  function Placeholder() {
    return (
      <S.Container onClick={handleClick}>
        <S.InnerContainer>
          {userInfo.id && <ImageInitials size={"mid"} {...userInfo} />}
          <S.Placeholder>
            {props.placeholder || "What's on your mind?"}
          </S.Placeholder>
          <S.PlusContainer>
            <I.Plus color={colours.marketingGreen} />
          </S.PlusContainer>
        </S.InnerContainer>
      </S.Container>
    );

    function handleClick() {
      if (userInfo.id) setIsOpen(true);
    }
  }

  function validateVideoUrl(url) {
    setVideo({});
    setVideoUrl(url);
    const id = getVideoId(url);
    if (id.service === "youtube") {
      setVideo({
        platform: "youtube",
        src: `https://www.youtube.com/embed/${id.id}?rel=0`,
        id: id.id,
      });
      setFiles([]);
      setVideoUrlVisible(false);
    }
    if (id.service === "vimeo") {
      setVideo({
        platform: "vimeo",
        src: `https://player.vimeo.com/video/${id.id}`,
        id: id.id,
      });
      setFiles([]);
      setVideoUrlVisible(false);
    }
  }
}

const S = {};

S.Container = styled.div`
  background-color: white;
  border: 1px solid ${u.colours.shellOverlay};
  border-radius: 4px;
  position: relative;
`;

S.InnerLoading = styled.div`
  align-items: center;
  backdrop-filter: blur(1px);
  background-color: rgba(248, 249, 250, 0.6);
  border-radius: 4px;
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 10;
`;

S.InnerContainer = styled.div`
  display: flex;
  padding: 10px 15px;
  ${(props) =>
    props.isOpen
      ? `
    border-bottom: 1px solid ${u.colours.shellOverlay};
    flex-direction: column;
  `
      : `
    align-items: center;
  `}
`;

S.PreviewContainer = styled.div`
  display: flex;
  ${(props) =>
    props.count < 2
      ? `
  `
      : `
    > div { &:nth-child(1) { margin-right: 10px; } }
  `}
`;

S.PreviewInner = styled.div`
  display: flex;
  flex: 1;
  position: relative;
`;

S.PreviewRemove = styled.div`
  align-items: center;
  background: rgb(228, 230, 235);
  border-radius: 14px;
  display: flex;
  height: 28px;
  justify-content: center;
  position: absolute;
  right: 6px;
  top: 6px;
  width: 28px;
  z-index: 1;
  :hover {
    cursor: pointer;
    background: rgb(208, 200, 205);
  }
`;

S.PreviewImage = styled.img`
  border-radius: 5px;
  object-fit: cover;
  object-position: 50% 50%;
  opacity: 0.9;
  width: 100%;
`;

S.BottomContainer = styled.div`
  align-items: center;
  display: flex;
  height: 50px;
  justify-content: space-between;
  padding: 0px 15px;
`;

S.IconsContainer = styled.div`
  display: flex;
`;

S.IconContainer = styled.div`
  align-items: center;
  border-radius: 17px;
  display: flex;
  height: 34px;
  justify-content: center;
  overflow: hidden;
  width: 34px;
  :hover {
    cursor: pointer;
    background: rgb(208, 200, 205);
  }
`;

S.HeaderContainer = styled.div`
  display: flex;
  margin-bottom: 15px;
`;

S.CloseContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  width: 28px;
  border-radius: 14px;
  background: rgb(249, 244, 241);
  :hover {
    cursor: pointer;
    background: rgb(208, 200, 205);
  }
`;

S.SubHeader = styled.div`
  flex: 1;
  margin-left: 10px;
`;

S.Text = styled.div`
  color: rgba(65, 65, 67, 0.6);
  font-size: 12px;
`;

S.Placeholder = styled.div`
  align-items: center;
  color: rgba(65, 65, 67, 0.6);
  cursor: pointer;
  display: flex;
  flex: 1;
  font-size: 16px;
  margin-left: 10px;
`;

S.PlusContainer = styled.div`
  height: 24px;
  width: 24px;
`;

// dedeupe one of these font sizes
S.TextArea = styled.textarea`
  border: none;
  color: ${u.colours.charcoal};
  font-family:
    HK Grotesk,
    sans-serif;
  font-size: 16px;
  font-size: 20px;
  margin: 15px 0px;
  min-height: 80px;
  outline: none;
  resize: none;
`;

S.PostButton = styled(Button)`
  padding: 8px 16px;
`;

S.InputFile = styled.input`
  display: none;
`;

S.InputVideoContainer = styled.div`
  height: ${(p) => (p.isVisible ? "70px" : "0px")};
  width: 100%;
  position: relative;
  overflow: hidden;
  padding: 0 10px 0 10px;
  transition: height linear 0.1s;
`;

S.InputVideo = styled(Input)`
  width: 100%;
`;

S.VideoPreviewContainer = styled.div`
  position: relative;
  padding-bottom: 56.25%; /* 16:9 */
  padding-top: 25px;
  height: 0;
`;

S.VideoPreview = styled.iframe`
  border: none;
  border-radius: 5px;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

S.Error = styled.div`
  color: red;
  font-family:
    HK Grotesk,
    sans-serif;
  font-size: 11px;
`;
